import { posService } from '@/services/pos.service';
import { mapDetailToPosVariant } from '@/pages/POS/utils/mapVariantDetail';
import { variantToOrderItem, recalcItem } from '@/pages/POS/utils/orderItemCalc';
import type { TenantOrder } from '@/mocks/orders';
import type {
  FulfillmentMode,
  OrderItem,
  PosVariantDetailItem,
  PosVariantDetailResponse,
} from '@/pages/POS/types';
import { readComboFields } from './mapOrderItemCombo';

type OrderLine = NonNullable<TenantOrder['items']>[number];

/** Chữ ký của `posService.lookupVariants` — tiêm được để test không cần mock module. */
export type LookupVariantsFn = (
  qrCodes: string[],
  fulfillmentType: FulfillmentMode,
) => Promise<PosVariantDetailResponse>;

export interface CopyOrderItemsResult {
  orderItems: OrderItem[];
  /** Tên các dòng KHÔNG tra được biến thể — đã BỎ khỏi giỏ, thu ngân phải thêm tay. */
  skipped: string[];
}

/**
 * Tab POS mới luôn mở ở chế độ Bán ngay (`createEmptyTab`) — mọi phép tính tiền
 * của dòng sao chép phải dùng đúng chế độ đó, không phải chế độ của đơn cũ.
 */
const COPY_FULFILLMENT: FulfillmentMode = 'INSTANT';

/**
 * Mã tra cứu của một dòng đơn đã lưu.
 *
 * `item.sku` là SKU của SẢN PHẨM (BE `pos-order.service` lưu `sku: product.sku`),
 * KHÔNG phải `variant_code` — tra bằng nó chỉ trúng khi sản phẩm một biến thể.
 * `product_variant_id` là thứ duy nhất trỏ 1-1 tới biến thể và BE lookup có
 * nhánh resolve theo id (`resolveBarcodeInputs`), nên ưu tiên tuyệt đối.
 */
function lookupCode(item: OrderLine): string {
  return item.product_variant_id || item.sku;
}

function findDetail(details: PosVariantDetailItem[], line: OrderLine, code: string) {
  return details.find((detail) => {
    // Khớp theo ID biến thể TRƯỚC: hai dòng cùng SẢN PHẨM nhưng khác biến thể
    // (màu/size) có cùng `product.sku`, nhánh so `sku` bên dưới sẽ gán CÙNG một
    // detail cho cả hai — sai giá, sai VAT. Id thì 1-1.
    if (line.product_variant_id && detail.variant.variant_id) {
      return String(detail.variant.variant_id) === line.product_variant_id;
    }
    return detail.variant.variant_code === code || detail.product.sku === line.sku;
  });
}

function hydrateLine(line: OrderLine, detail: PosVariantDetailItem): OrderItem {
  const variant = mapDetailToPosVariant(detail);
  // Dòng dựng bằng ĐÚNG đường mà thu ngân quét mã tạo ra: `variantCode` là mã
  // biến thể THẬT, `vat` là thuế suất hiện hành của sản phẩm, `retailPrice` là
  // giá lẻ hiện tại, `trackingMode` theo product master (không đoán từ serial).
  const item = variantToOrderItem(variant, COPY_FULFILLMENT);

  item.quantity = line.qty || 1;
  // KHÔNG chép serial: serial là MỘT vật phẩm cụ thể ĐÃ BÁN ở đơn cũ, không bán
  // lại được. `variantId` đã đúng nên thu ngân bấm chọn serial còn tồn ngay
  // trên dòng (SerialSwapDialog).
  item.specificationCode = undefined;

  Object.assign(item, readComboFields(line as unknown as Record<string, unknown>));

  // Dòng thuộc cụm combo giữ NGUYÊN giá đã phân bổ lúc bán: giá thành phần của
  // combo do `combo_definitions` quyết định, không phải giá lẻ. BE verify lại
  // `combo_unit_price` lúc tạo đơn và từ chối thẳng nếu combo đã đổi cấu hình —
  // sai lệch sẽ báo lỗi to, không âm thầm.
  //
  // Dòng thường thì NGƯỢC LẠI: giữ giá lẻ HIỆN TẠI (`variantToOrderItem` vừa
  // set), KHÔNG chép `line.price` của đơn cũ. Đây là một lần bán MỚI — bán vàng
  // hôm nay theo giá hôm qua là thất thu thật, và không có gì trên UI báo.
  if (item.comboGroupId) {
    item.price = line.price;
  }

  return recalcItem(item, COPY_FULFILLMENT);
}

/**
 * Dựng giỏ hàng cho tab POS sao chép từ một đơn ĐÃ LƯU.
 *
 * Vì sao phải async: dòng đơn đã lưu chỉ có `sku` (SKU sản phẩm) + `qty` +
 * `price`. Dựng `OrderItem` thẳng từ đó cho ra dòng thiếu `vat` (POS hiển thị
 * VAT = 0 trong khi BE vẫn tính VAT lúc tạo đơn ⇒ thu thiếu tiền), thiếu
 * `retailPrice` (mất phát hiện sửa giá) và mang `variantCode` SAI (BE resolve
 * `variant_code_serial` — nhầm biến thể hoặc 400 lúc thanh toán). Nên phải đi
 * qua đúng cổng mà POS vẫn dùng: `lookupVariants` → `mapDetailToPosVariant` →
 * `variantToOrderItem`, giống `Orders/edit.tsx`.
 *
 * Hai lời gọi lookup, và đó KHÔNG phải thừa:
 * - Dòng thường (`quantity`): tra ở INSTANT — đúng chế độ tab mới.
 * - Dòng serial: ở INSTANT, BE bắt buộc định dạng `variant_code_serial` và
 *   serial đó phải còn AVAILABLE (`getVariantDetails`, nhánh serial). Serial của
 *   đơn cũ đã bán ⇒ luôn lỗi. Chế độ PREORDER nhận mã biến thể trần cho MỌI
 *   tracking_mode và không kiểm tồn, nên đó là đường DUY NHẤT lấy được metadata
 *   biến thể cho dòng serial. Giá không lệch: cả hai nhánh BE đều trả
 *   `variant.unit_price`.
 *
 * Dòng tra không ra (SP ngừng bán / đổi mã / không cho bán POS) bị BỎ và trả về
 * ở `skipped` để caller báo tên cho thu ngân — thà thiếu một dòng nhìn thấy
 * được còn hơn một dòng mang mã sai đi tới bước thu tiền.
 */
export async function buildCopyOrderItems(
  order: TenantOrder,
  lookup: LookupVariantsFn = (codes, mode) => posService.lookupVariants(codes, mode),
): Promise<CopyOrderItemsResult> {
  const lines = order.items ?? [];
  if (lines.length === 0) return { orderItems: [], skipped: [] };

  const serialLines = lines.filter((line) => !!line.serial);
  const plainLines = lines.filter((line) => !line.serial);

  const [plainRes, serialRes] = await Promise.all([
    plainLines.length
      ? lookup(plainLines.map(lookupCode), COPY_FULFILLMENT)
      : Promise.resolve<PosVariantDetailResponse>({ items: [], item_errors: [] }),
    serialLines.length
      ? lookup(serialLines.map(lookupCode), 'PREORDER')
      : Promise.resolve<PosVariantDetailResponse>({ items: [], item_errors: [] }),
  ]);

  const orderItems: OrderItem[] = [];
  const skipped: string[] = [];

  for (const line of lines) {
    const code = lookupCode(line);
    // Tra trong ĐÚNG bộ kết quả của dòng: gộp hai bộ rồi khớp theo `product.sku`
    // có thể gán detail của dòng serial cho dòng thường cùng sản phẩm.
    const pool = line.serial ? serialRes.items : plainRes.items;
    const detail = findDetail(pool, line, code);
    if (!detail) {
      skipped.push(line.product_name || line.sku);
      continue;
    }
    orderItems.push(hydrateLine(line, detail));
  }

  return { orderItems, skipped };
}
